import React, { useState } from 'react';
import { CompassIcon, ClockIcon, CalendarIcon, FilterIcon, SearchIcon, BarChart2Icon, SortAscIcon, SortDescIcon } from 'lucide-react';
interface CustomerBrowsingHistoryProps {
  browsingHistory: any[];
}
export const CustomerBrowsingHistory: React.FC<CustomerBrowsingHistoryProps> = ({
  browsingHistory
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  // Apply filters and sorting
  const filteredHistory = browsingHistory.filter(item => {
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return item.page.toLowerCase().includes(term);
    }
    return true;
  }).sort((a, b) => {
    // Sort by selected field
    if (sortBy === 'date') {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    } else if (sortBy === 'duration') {
      const durationA = parseInt(a.duration.split('m')[0]) * 60 + parseInt(a.duration.split('m ')[1].split('s')[0]);
      const durationB = parseInt(b.duration.split('m')[0]) * 60 + parseInt(b.duration.split('m ')[1].split('s')[0]);
      return sortOrder === 'desc' ? durationB - durationA : durationA - durationB;
    } else {
      // Sort by page name
      const pageA = a.page.toLowerCase();
      const pageB = b.page.toLowerCase();
      return sortOrder === 'desc' ? pageB.localeCompare(pageA) : pageA.localeCompare(pageB);
    }
  });
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'desc' ? <SortDescIcon className="h-4 w-4 ml-1" data-id="element-1701" /> : <SortAscIcon className="h-4 w-4 ml-1" data-id="element-1702" />;
  };
  // Group browsing history by date
  const groupedHistory: {
    [key: string]: any[];
  } = {};
  filteredHistory.forEach(item => {
    const date = formatDate(item.date);
    if (!groupedHistory[date]) {
      groupedHistory[date] = [];
    }
    groupedHistory[date].push(item);
  });
  return <div className="p-6" data-id="element-1703">
      <div className="flex justify-between items-center mb-6" data-id="element-1704">
        <h2 className="text-lg font-medium text-gray-800" data-id="element-1705">Browsing History</h2>
        <div className="flex items-center space-x-2" data-id="element-1706">
          <span className="text-sm text-gray-500" data-id="element-1707">
            {browsingHistory.length} total views
          </span>
          <button className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1708">
            <BarChart2Icon className="h-4 w-4 mr-2" data-id="element-1709" />
            Analytics
          </button>
        </div>
      </div>
      {/* Filters */}
      <div className="flex gap-4 mb-6" data-id="element-1710">
        <div className="relative flex-1" data-id="element-1711">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1712">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1713" />
          </div>
          <input type="text" placeholder="Search pages..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1714" />
        </div>
      </div>
      {/* Browsing history list */}
      {filteredHistory.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg" data-id="element-1715">
          <CompassIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" data-id="element-1716" />
          <h3 className="text-lg font-medium text-gray-800 mb-2" data-id="element-1717">
            No browsing history found
          </h3>
          <p className="text-gray-500" data-id="element-1718">Try adjusting your search</p>
        </div> : <div className="space-y-6" data-id="element-1719">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1720">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1721">
              <thead className="bg-gray-50" data-id="element-1722">
                <tr data-id="element-1723">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('page')} data-id="element-1724">
                    <div className="flex items-center" data-id="element-1725">
                      Page
                      {getSortIcon('page')}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('date')} data-id="element-1726">
                    <div className="flex items-center" data-id="element-1727">
                      Date
                      {getSortIcon('date')}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('duration')} data-id="element-1728">
                    <div className="flex items-center" data-id="element-1729">
                      Duration
                      {getSortIcon('duration')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1730">
                {filteredHistory.map(item => <tr key={item.id} className="hover:bg-gray-50" data-id="element-1731">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1732">
                      <div className="flex items-center" data-id="element-1733">
                        <div className="flex-shrink-0 h-8 w-8 rounded bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-1734">
                          <CompassIcon className="h-4 w-4" data-id="element-1735" />
                        </div>
                        <div className="ml-3" data-id="element-1736">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1737">
                            {item.page}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1738">
                      <div className="flex items-center text-sm text-gray-900" data-id="element-1739">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1740" />
                        {formatDate(item.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1741">
                      <div className="flex items-center text-sm text-gray-900" data-id="element-1742">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1743" />
                        {item.duration}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* Analytics section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1744">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-1745">
              Browsing Insights
            </h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-1746">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-id="element-1747">
                <p className="text-sm text-blue-800 mb-1" data-id="element-1748">
                  Most Viewed Category
                </p>
                <p className="text-lg font-bold text-blue-900" data-id="element-1749">
                  {(() => {
                const categories = filteredHistory.map(item => {
                  if (item.page.includes('Europe')) return 'Europe';
                  if (item.page.includes('Asia') || item.page.includes('Japan') || item.page.includes('Bali')) return 'Asia';
                  if (item.page.includes('Caribbean')) return 'Caribbean';
                  if (item.page.includes('Adventure')) return 'Adventure';
                  if (item.page.includes('Wellness') || item.page.includes('Spa')) return 'Wellness';
                  return 'Other';
                });
                const categoryCounts: {
                  [key: string]: number;
                } = {};
                categories.forEach(cat => {
                  categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                });
                const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];
                return topCategory ? topCategory[0] : 'None';
              })()}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-100" data-id="element-1750">
                <p className="text-sm text-green-800 mb-1" data-id="element-1751">
                  Total Browsing Time
                </p>
                <p className="text-lg font-bold text-green-900" data-id="element-1752">
                  {(() => {
                let totalSeconds = 0;
                filteredHistory.forEach(item => {
                  const [minutes, secondsPart] = item.duration.split('m ');
                  const seconds = parseInt(secondsPart);
                  totalSeconds += parseInt(minutes) * 60 + seconds;
                });
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor(totalSeconds % 3600 / 60);
                return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
              })()}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100" data-id="element-1753">
                <p className="text-sm text-purple-800 mb-1" data-id="element-1754">Recent Interest</p>
                <p className="text-lg font-bold text-purple-900" data-id="element-1755">
                  {filteredHistory.length > 0 ? filteredHistory[0].page.split(' ')[0] : 'None'}
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};